import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';

import RefreshIcon from '@material-ui/icons/Refresh'

import Loading from 'app/components/loaders/Loading';

const FilterOptions = [
    {id: 'results_by_option', label: 'Results by Option'},
    {id: 'results_by_category', label: 'Results by Category'},
    {id: 'results_by_day', label: 'Results by Day'},
];

const useStyles = makeStyles(theme => ({
    noData: {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.grey[100]
    },
    empty: {
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(6),
        paddingRight: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        textAlign: 'center',
        backgroundColor: theme.palette.grey[100]
    },
    paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        marginBottom: theme.spacing(4),
    },
    divider: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    barContainer: {
        height: 400,
        marginBottom: theme.spacing(3),
    },
    rotate: {
        animation: 'spin 1s infinite linear'
    },
    noRotate: {

    },
    filter: {
        marginRight: theme.spacing(3),
        verticalAlign: 'middle'
    },
    iconButton: {
        verticalAlign: 'middle'
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    blue: {
        color: theme.palette.primary.main
    }
}));

const resultsBar = (data, styles, filterOption) => {

    const axisBottomRotation = -30;

    const axisBottomLegend = () => {
        switch (filterOption) {
            case 'results_by_day':
                return 'Days';
            case 'results_by_option':
                return 'Options';
            case 'results_by_category':
                return 'Categories';
        }
    }

    if (data) {
        return (
            <ResponsiveBar
                data={data}
                indexBy="title"
                keys={[ 'votes' ]}
                colorBy='index'
                margin={{ top: 24, right: 0, bottom: 80, left: 50 }}
                padding={0.3}
                colors={{ scheme: 'paired' }}
                borderColor={{ from: 'color', modifiers: [ [ 'darker', 1.6 ] ] }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: axisBottomRotation,
                    legend: axisBottomLegend(),
                    legendPosition: 'middle',
                    legendOffset: 64
                }}
                axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: '# of Votes',
                    legendPosition: 'middle',
                    legendOffset: -44
                }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelTextColor={{ from: 'color', modifiers: [ [ 'darker', 1.6 ] ] }}
                minValue={0}
                animate={true}
                motionStiffness={90}
                motionDamping={15}
            />
        )
    } else {
        return (
            <Typography variant="h6" color="textSecondary" className={ styles.noData }>
                <span>Not enough data. Please try again later.</span>
            </Typography>
        )
    }
}

export default function Voting(props) {

    const [ users, setUsers ] = useState([]);
    const [ filterOption, setFilterOption ] = useState('results_by_option');

    const [ resultsByOption, setResultsByOption ] = useState([]);
    const [ resultsByCategory, setResultsByCategory ] = useState([]);
    const [ resultsByDay, setResultsByDay ] = useState([]);

    const { vote, loading, results, refreshResults } = props;

    let theme = useTheme();

    const classes = useStyles();

    useEffect(() => {
        if (results.analytics && results.analytics.results_by_option) {
            setResultsByOption(results.analytics.results_by_option);
        } else {
            setResultsByOption([]);
        }
        if (results.analytics && results.analytics.results_by_category) {
            setResultsByCategory(results.analytics.results_by_category);
        } else {
            setResultsByCategory([]);
        }
        if (results.analytics && results.analytics.results_by_day) {
            setResultsByDay(results.analytics.results_by_day);
        } else {
            setResultsByDay([]);
        }
    }, [results])

    const memoizedResultsBar = useMemo(() => resultsBar(
        results.analytics[filterOption], classes, filterOption),
        [results.analytics[filterOption], classes, filterOption]
    );

    // Setter Functions ------------------------------------------

    const handleFilterOption= e => {
        const value = e.target.value;
        setFilterOption(value);
    }

    // general functions ----------------------------------------

    const filterLabel = () => {
        return FilterOptions.find(option => option.id === filterOption).label;
    }

    // Exporting
    const exportResultUsers = () => {

        var f = document.createElement("form");
        f.method = "POST";
        f.action = "/admin/export/vote";
        f.target = "_blank";

        var token = document.createElement("input");
        token.name = "_token";
        token.type = "hidden";
        token.value = props.csrf;

        var data = document.createElement("input");
        data.name = "data";
        data.type = "hidden";
        data.value = JSON.stringify(results.users.length ? results.users : []);

        var title = document.createElement("input");
        title.name = "title";
        title.type = "hidden";
        title.value = vote.topic + ' - confirmed voted users';

        f.appendChild(token);
        f.appendChild(data);
        f.appendChild(title);
        document.body.appendChild(f);
        f.submit();
    }

    // Rendering Functions ------------------------------------------

    if (!vote.published) {
        return (
            <Paper className={classes.paper}>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={2}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3">
                            Results
                        </Typography>
                    </Grid>
                </Grid>
                <div className="p-t-xl"></div>
                <Typography variant="h6" color="textSecondary" className={ classes.empty }>
                    Please publish the Vote to view the results.
                </Typography>
            </Paper>
        )
    } else {
        return (
            <Paper className={classes.paper}>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={2}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3">
                            Results
                        </Typography>
                    </Grid>
                    <Grid>
                        <Tooltip title="Refresh Results">
                            <IconButton
                                aria-label="Refresh Results"
                                onClick={refreshResults}
                                >
                                <RefreshIcon className={loading ? classes.rotate : classes.noRotate} fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Divider className={classes.divider}/>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={2}
                    >
                    <Grid item>
                        <Typography variant="subtitle2" component="h5">
                            Graph
                        </Typography>
                    </Grid>
                    <Grid item>
                        <TextField
                            id="change-graph-view"
                            select
                            label="Graph View"
                            value={filterOption}
                            onChange={handleFilterOption}
                            variant="outlined"
                            margin="normal"
                            size="small"
                            >
                            {FilterOptions.map(option => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
                <div className={classes.barContainer}>
                    {/*memoizedResultsBar*/}
                </div>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2" component="h5">
                            Breakdown by Option
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2" component="h5">
                            Breakdown by Category
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        {resultsByOption.length ? resultsByOption.map((option, i) => (
                            <Typography variant="subtitle2" color="textSecondary" key={i}>
                                {option.title} - <Typography variant="subtitle2" color="primary" component="span">
                                    {option.votes}
                                </Typography>
                            </Typography>
                        )) : (
                            <Typography variant="h6" color="textSecondary" className={ classes.empty }>
                                No option results to show at this time.
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        {resultsByCategory.length ? resultsByCategory.map((option, i) => (
                            <Typography variant="subtitle2" color="textSecondary" key={i}>
                                {option.title} - <Typography variant="subtitle2" color="primary" component="span">
                                    {option.votes}
                                </Typography>
                            </Typography>
                        )) : (
                            <Typography variant="h6" color="textSecondary" className={ classes.empty }>
                                No categorized results to show at this time.
                            </Typography>
                        )}
                    </Grid>
                </Grid>
                <div className={classes.marginTop}/>
                {/*}
                <Table
                    title="Users"
                    rows={[
                        { id: 'name', align: 'left', disablePadding: true, label: 'Name' },
                        { id: 'voted_at', align: 'right', disablePadding: true, label: 'Date Voted' },
                    ]}
                    viewableRowIds = {['name', 'voted_at']}
                    data={results.users.length ? results.users : []}
                    searchRow="name"
                    orderBy="name"
                    order="asc"
                    onReloadData={refreshResults}
                    disableActions
                    >
                </Table>

                */}
                <Grid container spacing={2} justify="space-between">
                    <Grid item></Grid>
                    <Grid item>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={exportResultUsers}
                            >
                            Export Users
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
    )};
}
